import cx from "classnames";
/* eslint-disable max-lines */
import React, { RefObject, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Container, Row, XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import {
  BasicButton,
  BasicIconButton,
  BasicSinglelineTextInput,
  ButtonType,
} from "@gaming/xds-react-js";
import { XfFilter } from "@gaming/xds-react-js-icons";

import { ContentBox, ContentBoxIcon, GameConceptListFilterButton } from "../";
import { DefaultNewGameConceptId } from "../../constants";
import { GameConceptFilter, usePaginatedGameConcepts } from "../../helpers/gameConceptHelpers";
import { getDashboardUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { aaaGameConceptsActions } from "../../redux/actions/aaaActions";
import { getIsLoadingGameConcept } from "../../redux/selectors";
import { getAAAGameConceptsQuery } from "../../redux/selectors/crmStatusSelectors";
import { PaginatedConceptData } from "../../server/crm/api/responses/getFullPaginatedConceptsResponse";
import * as styles from "./AaaGameConceptsList.module.css";
import { GameConceptFilterPopup } from "./GameConceptFilterPopup";
import { GameConceptRow } from "./GameConceptsRow";

export type AaaGameConceptsListProps = Readonly<{
  programDashboardRouteName: RouteNames;
}>;

export const AaaGameConceptsList = ({ programDashboardRouteName }: AaaGameConceptsListProps) => {
  const [
    welcomePageGameConceptsListEmpty,
    myGameApplication,
    newApplication,
    submitted,
    draft,
    gameConceptsListSearchBox,
    previousPage,
    nextPage,
  ] = useStrings(
    "welcomePageGameConceptsListEmpty",
    "myGameApplication",
    "newApplication",
    "submitted",
    "draft",
    "gameConceptsListSearchBox",
    "previousPage",
    "nextPage"
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const gameConceptsQueries = useSelector(getAAAGameConceptsQuery);
  const [paginatedInformation, setPaginatedInformation] = useState({
    currentPage: 1,
    orderBy: "",
    filterBy: [GameConceptFilter.Draft],
    searchBy: "",
  } as PaginatedConceptData);
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);

  const [isInitialCall, setIsInitialCall] = useState(true);
  const totalPages = Number(gameConceptsQueries?.totalPages);
  const isLastPage = gameConceptsQueries ? totalPages === paginatedInformation.currentPage : false;
  const isFirstPage = paginatedInformation.currentPage === 1;

  const isLoadingGameConcepts = useSelector(getIsLoadingGameConcept);

  const [isInProgressSelected, setIsInProgressSelected] = useState(true);

  const inProgressFilterChange = (inProgressSelected: boolean): void => {
    setIsInProgressSelected(inProgressSelected);
    setPaginatedInformation({
      currentPage: 1,
      orderBy: "",
      filterBy: inProgressSelected
        ? [GameConceptFilter.Draft]
        : [
            GameConceptFilter.UnderReview,
            GameConceptFilter.Approved,
            GameConceptFilter.NeedMoreInfo,
            GameConceptFilter.Rejected,
          ],
      searchBy: "",
    });
  };

  const onGameConceptDelete = (): void => {
    if (paginatedInformation.currentPage > 1) {
      setPaginatedInformation({
        currentPage: 1,
        orderBy: "",
        filterBy: isInProgressSelected
          ? [GameConceptFilter.Draft]
          : [
              GameConceptFilter.UnderReview,
              GameConceptFilter.Approved,
              GameConceptFilter.NeedMoreInfo,
              GameConceptFilter.Rejected,
            ],
        searchBy: "",
      });
    }
  };

  useEffect(() => {
    if ((!gameConceptsQueries && isInitialCall) || (gameConceptsQueries && !isInitialCall)) {
      dispatch(aaaGameConceptsActions.aaaGetConcepts.start(paginatedInformation));
    }
    setIsInitialCall(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedInformation, dispatch]);

  useEffect(
    () => () => {
      // Clear query on unmount (this will act as a flag if the user were to navigate back to this page)
      dispatch(aaaGameConceptsActions.clearGameConceptListQuery(Programs.Aaa));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const gameConcepts = usePaginatedGameConcepts();
  const gotGameConcepts = gameConcepts && gameConcepts.length > 0;
  const filterButtonRef: RefObject<HTMLElement> = useRef(null);

  return (
    <ContentBox
      titleConfiguration={{
        title: myGameApplication,
        rightSideLink: {
          text: newApplication,
          onClick: () =>
            history.push(getDashboardUrl(programDashboardRouteName, DefaultNewGameConceptId)),
          icon: ContentBoxIcon.Add,
        },
      }}
      className={styles.contentBox}
    >
      <div className={styles.tabContainer}>
        <GameConceptListFilterButton
          title={draft}
          isActive={isInProgressSelected}
          onClick={() => inProgressFilterChange(true)}
        />
        <GameConceptListFilterButton
          title={submitted}
          isActive={!isInProgressSelected}
          onClick={() => inProgressFilterChange(false)}
        />
      </div>
      <div className={styles.searchWrapper}>
        <BasicSinglelineTextInput
          value={paginatedInformation.searchBy}
          className={styles.outerSearchBox}
          inputClassName={styles.innerSearchBox}
          placeholder={gameConceptsListSearchBox}
          onChange={e => {
            setPaginatedInformation({
              ...paginatedInformation,
              searchBy: e.target.value,
              currentPage: 1,
            });
          }}
        />
        {!isInProgressSelected && (
          <BasicIconButton
            as="button"
            buttonType={ButtonType.Secondary}
            className={styles.filterButton}
            icon={XfFilter}
            onClick={() => setIsFilterPopupOpen(prevIsFilterPopupOpen => !prevIsFilterPopupOpen)}
            ref={filterButtonRef}
          />
        )}
        <GameConceptFilterPopup
          anchorRef={filterButtonRef}
          isOpen={isFilterPopupOpen}
          onClose={() => setIsFilterPopupOpen(false)}
          onFilterSelected={filters => {
            setPaginatedInformation({
              ...paginatedInformation,
              filterBy: filters,
              currentPage: 1,
            });
          }}
        />
      </div>
      <ContentBox className={styles.gameConceptsContainer}>
        {isLoadingGameConcepts && (
          <div className={styles.spinnerWrapper}>
            <XboxSpinner />
          </div>
        )}
        {!isLoadingGameConcepts && !gotGameConcepts && (
          <p className={styles.noGameConceptsMessage}>{welcomePageGameConceptsListEmpty}</p>
        )}
        {gameConcepts?.map((gameConcept, index) => (
          <GameConceptRow
            gameConcept={gameConcept}
            programDashboardRouteName={programDashboardRouteName}
            key={`${index}-${gameConcept.id}`}
            index={index}
            className={isLoadingGameConcepts ? styles.hidden : ""}
            onDelete={onGameConceptDelete}
          />
        ))}
      </ContentBox>
      {gotGameConcepts && !isNaN(totalPages) && totalPages > 1 && (
        <Container>
          <Row className={styles.navButtonsContainer}>
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              disabled={isFirstPage}
              className={cx(styles.navigationButton, isFirstPage ? styles.disabledNavButton : "")}
              onClick={() =>
                setPaginatedInformation({
                  ...paginatedInformation,
                  currentPage: isFirstPage ? 1 : paginatedInformation.currentPage - 1,
                })
              }
              textDecorationUnderline={true}
            >
              {previousPage}
            </BasicButton>
            <BasicButton
              as="button"
              buttonType={ButtonType.Secondary}
              disabled={isLastPage}
              className={cx(styles.navigationButton, isLastPage ? styles.disabledNavButton : "")}
              onClick={() =>
                setPaginatedInformation({
                  ...paginatedInformation,
                  currentPage: paginatedInformation.currentPage + 1,
                })
              }
              textDecorationUnderline={true}
            >
              {nextPage}
            </BasicButton>
          </Row>
        </Container>
      )}
    </ContentBox>
  );
};
